import React, {useState, useLayoutEffect, useRef} from "react";
import {BetStatusButton} from "../BetStatusButton";
import {ButtonBase} from "../../elements";
import {BoxWrpr} from "./BoxWrpr";
import {format} from "date-fns";
import {FixtureElement, PeriodScoreElement, StepForm} from "../../../models/api/codegen/code";
import {betStatusEnum} from "../CommonTypes";
import {useTranslation} from "react-i18next";
import {ColoredIcon} from "../../Steps/step-parts-components/SportIconAndTitle";
import {MoreLessToggle} from "../../custom/MoreLessToggleInner";

interface MyBetsRowProps {
    step: StepForm,
    index: number | string,
    isChildRow?: boolean
    parentStatus?: betStatusEnum;

}

export function MyBetsRow(props: MyBetsRowProps) {
    const {step, index, isChildRow, parentStatus} = props;
    const [showChildStep, setShowChildStep] = useState<boolean>(false);
    const {t} = useTranslation();

    const formatDate = (stringDate: string | undefined) =>
        stringDate ? format(new Date(stringDate), 'HH:mm dd.MM.yyyy') : '';

    function generateTitle(fixture: FixtureElement) {
        return ' (' + fixture.tn + ')';
    }

    const fixtureNameRef = useRef<HTMLDivElement>(null);
    const [isFixtureNameTruncated, setIsFixtureNameTruncated] = useState(false);

    useLayoutEffect(() => {
        const check = () => {
            if (!step.fixture || !fixtureNameRef.current) return;

            const el = fixtureNameRef.current;
            if (el.offsetWidth > 0) {
            setIsFixtureNameTruncated(el.scrollHeight > el.clientHeight);
            }
        };

        check();
        const fallback = setTimeout(check, 100);
        window.addEventListener('resize', check);
        return () => {
            clearTimeout(fallback);
            window.removeEventListener('resize', check);
        }
    }, [step.fixture?.cn, step.fixture?.cl]);

    const Score: React.FC<{ homeScore?: number, awayScore?: number, periodScores?: Array<PeriodScoreElement> }> = ({homeScore, awayScore, periodScores}) => {
        return (
            <>
                {!(homeScore == null || awayScore == null) &&
                <span className="ml-[15px] text-[#F97624]">{homeScore} : {awayScore} </span>}

                {periodScores && periodScores.length != 0 &&
                <span>( {periodScores.map((key) => `${key.homeScore} : ${key.awayScore}`).join(', ')} )</span>}

            </>
        )
    };

    return (
        <>
            <div className={`flex rounded-[8px] bg-[${isChildRow ? '#171A1E' : '#1b1e23'}] mt-[1px] py-[9px] px-[24px] items-center ${isChildRow ? 'ml-[50px]' : ''}`}>
                {!isChildRow && (
                    <div className="flex flex-grow-[1] basis-0 max-w-[50px] text-center">{index}</div>
                )}


                <BoxWrpr fg={2}>
                    {step.fixture && <ColoredIcon color={step.fixture.sc} imageUrl={step.fixture.siu}/>}
                    {step.fixture ? step.fixture.sn : null}
                </BoxWrpr>
                <div className="flex flex-grow-[2] basis-0 flex-col">
                    <div>{step.id}</div>
                    <div className="text-[#ABABAB]">{formatDate(step.date)}</div>
                </div>
                <div className="flex flex-grow-[4] basis-0 flex-col">
                    {step.type == 'ORDINAR' || isChildRow ?
                        <>
                            <div className="text-[#e7e7e7] font-['Roboto'] text-[16px] leading-[20px]">
                                {step.market} - {step.selection}
                            </div>
                            <div className="block netbook:!hidden">
                                {step.fixture && (
                                <>
                                    <div className="text-[#e7e7e7] font-['Roboto'] text-[16px] leading-[20px] font-medium">
                                    {step.fixture.cn}
                                    {!step.fixture.cl && ` ${generateTitle(step.fixture)}`}
                                    </div>
                                </>
                                )}
                            </div>

                            <div className="hidden netbook:!block">
                                {step.fixture ? (
                                <div className="relative max-w-full group">
                                    <div
                                    ref={fixtureNameRef}
                                    className="text-[#e7e7e7] font-['Roboto'] text-[16px] leading-[20px] font-medium overflow-hidden"
                                    style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical' as any,
                                    }}
                                    >
                                    {step.fixture.cn}
                                    {!step.fixture.cl && ` ${generateTitle(step.fixture)}`}
                                    </div>
                                    {isFixtureNameTruncated && (
                                    <div className="absolute top-0 left-0 bg-[#252930] text-[#42B6F6] px-2 rounded text-[16px] z-10 max-w-[407px] min-h-[40px] w-max line-clamp-2 overflow-hidden pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        {step.fixture.cn}
                                        {!step.fixture.cl && ` ${generateTitle(step.fixture)}`}
                                    </div>
                                    )}
                                </div>
                                ) : null}
                            </div>

                            <div className="text-[#ababab] font-['Roboto'] text-[16px] leading-[20px] tracking-[0.67px]">
                                {step.fixture?.st && formatDate(step.fixture.st)}
                                {step.fixture?.ms && (
                                <Score
                                    homeScore={step.fixture.ms.homeScore}
                                    awayScore={step.fixture.ms.awayScore}
                                    periodScores={step.fixture.ms.periodScores}
                                />
                                )}
                            </div>
                        </>
                        :
                        <ButtonBase
                            onClick={() => setShowChildStep(prevState => !prevState)}
                            className="p-[20px] rounded-[8px] border border-[#EACD40] text-[#EACD40] w-full flex justify-between"
                        >
                            {t(`coupons.${step.type}.tabTitle`)}
                            {' '}{step.steps && step.steps.length}
                            <MoreLessToggle isOpen={showChildStep}/>
                        </ButtonBase>
                    }
                </div>
                <BoxWrpr fg={1} center>{step.type !== 'SYSTEM' && step.odds}</BoxWrpr>
                <BoxWrpr fg={1} center>{step.rate}</BoxWrpr>
                <BoxWrpr fg={1} center>{step.win === 0 ? '0.00' : step.win}</BoxWrpr>
                <BoxWrpr fg={2}>
                    <BetStatusButton calculated={step.calculated}
                                     betStatus={step.status}
                                     winning={step.winning}
                                     type={step.type}
                                     isChild={isChildRow ? isChildRow : false}
                                     parentStatus={parentStatus}
                                     steps={step.type === 'EXPRESS' ? step.steps : null}
                    />
                </BoxWrpr>
            </div>
            {(step.steps && showChildStep) &&
                <>
                    {step.steps.map((childStep, childIndex) =>
                        <MyBetsRow step={childStep} index={childIndex}
                                   isChildRow={true}
                                   key={step.id + '_' + childIndex}
                                   parentStatus={step.status}
                        />
                    )}
                </>
            }
        </>
    )
}
